import { useProfileShareLink } from "~vue/CollaborationProfile/composables/useProfileShareLink";
import { type ProfileShareSource, withShareAttribution } from "~vue/CollaborationProfile/profileShareAttribution";
import type { ComputedRef, Ref } from "vue";

export const PROFILE_SHARE_MESSAGE = "Hey, here is my Collaboration Profile, including my Perspective results when available, so you know how to work with me. Send me yours back?";
export const PROFILE_SHARE_EMAIL_SUBJECT = "My Collaboration Profile";

export const profileShareBackMessage = (firstName: string): string =>
    `Hi ${firstName}! Thanks for sharing your Collaboration Profile. I wanted to share mine, including my Perspective results when available, with you, too:`;

export const profileInviteMessage = (firstName = ""): string =>
    `Hi${firstName.trim() ? ` ${firstName.trim()}` : ""}, can you set up or share your Collaboration Profile, including your Perspective results when available? It gives me a much better sense of how you like to work!`;

export type { ProfileShareTarget } from "~vue/CollaborationProfile/profileShareAttribution";

const SLACK_OPEN_DELAY_MS = 1000;

const teamsShareHref = (url: string, message: string = PROFILE_SHARE_MESSAGE): string =>
    `https://teams.microsoft.com/share?href=${encodeURIComponent(url)}&msgText=${encodeURIComponent(message)}`;

export const openTeamsShare = (url: string, message: string = PROFILE_SHARE_MESSAGE): void => {
    globalThis.open(teamsShareHref(url, message), "_blank", "noopener");
};

export const copyShareUrl = async (url: string, message: string = PROFILE_SHARE_MESSAGE): Promise<boolean> => {
    try {
        await navigator.clipboard.writeText(`${message}\n\n${url}`);
        return true;
    } catch {
        return false;
    }
};

type ProfileShareActionsArgs = {
    profileId: ComputedRef<string | undefined>;
    // Accepts a writable ref (dialog's local access) or a computed (the card's derived access).
    access: Ref<string>;
    // Absolute URL to the user's own collaboration profile — used for organization-wide access
    // (no per-invite link) when sharing from a page other than the profile page itself.
    ownProfileUrl?: ComputedRef<string | undefined>;
    // An existing active per-invite share link, when the caller already has one.
    activeShareUrl?: ComputedRef<string | undefined>;
    shareSource: ProfileShareSource;
};

export const useProfileShareActions = ({ profileId, access, ownProfileUrl, activeShareUrl, shareSource }: ProfileShareActionsArgs) => {
    const { isGeneratingLink, shareLinkError, isCopied, ensureShareLink, handleCopyLink } = useProfileShareLink({
        profileId,
        activeShareUrl,
        ownProfileUrl,
        shareSource,
    });

    const copyLink = (message?: string): Promise<boolean> => handleCopyLink(access.value, undefined, message);

    // Copy the message + URL, then open Slack so the user can paste it.
    const shareViaSlack = (): Promise<boolean> =>
        handleCopyLink(access.value, () => setTimeout(() => globalThis.open("slack://", "_blank"), SLACK_OPEN_DELAY_MS), PROFILE_SHARE_MESSAGE, "slack");

    const shareViaTeams = async (message: string = PROFILE_SHARE_MESSAGE): Promise<boolean> => {
        shareLinkError.value = null;
        const url = await ensureShareLink(access.value);
        if (!url) {
            return false;
        }
        openTeamsShare(withShareAttribution(url, shareSource, "teams"), message);
        return true;
    };

    const shareViaEmail = async (recipientEmail?: string | null): Promise<boolean> => {
        shareLinkError.value = null;
        const url = await ensureShareLink(access.value);
        if (!url) {
            return false;
        }
        const body = `${PROFILE_SHARE_MESSAGE}\n\n${withShareAttribution(url, shareSource, "email")}`;
        const to = recipientEmail ? encodeURIComponent(recipientEmail) : "";
        globalThis.location.href = `mailto:${to}?subject=${encodeURIComponent(PROFILE_SHARE_EMAIL_SUBJECT)}&body=${encodeURIComponent(body)}`;
        return true;
    };

    return {
        isGeneratingLink,
        shareLinkError,
        isCopied,
        copyLink,
        shareViaSlack,
        shareViaTeams,
        shareViaEmail,
    };
};
